import React, { useState } from "react";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  IconButton,
  styled,
  Tooltip,
  useTheme,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  FormControl,
  CircularProgress,
} from "@mui/material";
import { Edit } from "@mui/icons-material";
import { toast } from "react-hot-toast";
import apiService from "Network/apiService";
import { API_ENDPOINTS } from "Network/apiEndpoints";
import AddMemberModal from "./AddMemberModal";
import { DatePicker } from "@mui/x-date-pickers";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";

// Styled components with theme-aware colors
const StyledTableCell = styled(TableCell)(({ theme }) => ({
  padding: "16px 24px",
  color: theme.palette.mode === 'dark' ? theme.palette.common.white : theme.palette.common.black,
  borderBottom: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'}`,
  "&.header": {
    fontWeight: 600,
    fontSize: "0.875rem",
  },
  "&:first-of-type": {
    paddingLeft: 32,
  },
  "&:last-of-type": {
    paddingRight: 32,
  },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  "&:hover": {
    backgroundColor: theme.palette.mode === 'dark' 
      ? "rgba(255, 255, 255, 0.05)" 
      : "rgba(0, 0, 0, 0.05)",
  },
  "& > .MuiTableCell-root": {
    borderBottom: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'}`,
  },
}));

const RenewalModal = ({ open, onClose, memberId, onSuccess }) => {
  const theme = useTheme();
  const [purchaseDate, setPurchaseDate] = useState(new Date());
  const [subscriptionYear, setSubscriptionYear] = useState("1");
  const [remarks, setRemarks] = useState("");

  const handleSubmit = async () => {
    try {
      const payload = {
        member_id: memberId,
        purchase_date: purchaseDate.toISOString().split('T')[0],
        subscription_year: subscriptionYear,
        remarks: remarks,
      };
      
      const response = await apiService.post(API_ENDPOINTS.POST_renew_member, payload);
      if (response?.success) {
        toast.success("Membership renewed successfully");
        onSuccess();
        onClose();
      } else {
        toast.error("Failed to renew membership");
      }
    } catch (error) {
      console.error("Error renewing membership:", error);
      toast.error("Error renewing membership");
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      PaperProps={{
        sx: {
          backgroundColor: theme.palette.mode === 'dark' ? 'background.paper' : 'common.white',
          minWidth: 400,
        }
      }}
    >
      <DialogTitle sx={{ color: theme.palette.mode === 'dark' ? 'common.white' : 'common.black' }}>
        Renew Membership
      </DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DatePicker
              label="Purchase Date"
              value={purchaseDate}
              onChange={(newValue) => setPurchaseDate(newValue)}
              sx={{ width: '100%' }}
            />
          </LocalizationProvider>

          <TextField
            select
            label="Subscription Plan"
            value={subscriptionYear}
            onChange={(e) => setSubscriptionYear(e.target.value)}
            fullWidth
          >
            <MenuItem value="1">1 Year</MenuItem>
            <MenuItem value="2">2 Years</MenuItem>
            <MenuItem value="3">3 Years</MenuItem>
          </TextField>

          <TextField
            label="Remarks"
            multiline
            rows={3}
            value={remarks}
            onChange={(e) => setRemarks(e.target.value)}
            fullWidth
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={handleSubmit} variant="contained" color="primary">
          Renew Membership
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const MembersDataTable = ({ data, isLoading, onRefresh, chapterId }) => {
  const [openMemberModal, setOpenMemberModal] = useState(false);
  const [editData, setEditData] = useState(null);
  const [openRenewalModal, setOpenRenewalModal] = useState(false);
  const [selectedMemberId, setSelectedMemberId] = useState(null);
  const theme = useTheme();

  const handleEditClick = async (memberId) => {
    try {
      const response = await apiService.get(`${API_ENDPOINTS.GET_member}?member_id=${memberId}`);
      if (response?.success) {
        setEditData(response.data);
        setOpenMemberModal(true);
      } else {
        toast.error("Failed to fetch member details");
      }
    } catch (error) {
      console.error("Error fetching member details:", error);
      toast.error("Error fetching member details");
    }
  };

  const handleRenewalClick = (memberId) => {
    setSelectedMemberId(memberId);
    setOpenRenewalModal(true);
  };

  return (
    <Box sx={{ width: "100%" }}>
      <TableContainer 
        component={Paper} 
        sx={{ 
          backgroundColor: theme.palette.mode === 'dark' ? 'background.paper' : 'common.white',
          '& .MuiTable-root': {
            borderSpacing: '0 4px',
            borderCollapse: 'separate',
          }
        }}
      >
        <Table>
          <TableHead>
            <TableRow>
              <StyledTableCell className="header">Profile Image</StyledTableCell>
              <StyledTableCell className="header">Member Name</StyledTableCell>
              
              <StyledTableCell className="header">Business Done</StyledTableCell>
              <StyledTableCell className="header">Referrals Pass</StyledTableCell>
              <StyledTableCell className="header">One to One's</StyledTableCell>
              <StyledTableCell className="header">Expiry Date</StyledTableCell>
              <StyledTableCell className="header" align="center">Action</StyledTableCell>
            </TableRow>
          </TableHead>

          <TableBody>
          {isLoading ? (
              <TableRow>
                <TableCell colSpan={6} sx={{ border: 'none' }}>
                  <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
                    <CircularProgress />
                  </Box>
                </TableCell>
              </TableRow>
            ) : data.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} sx={{ border: 'none' }}>
                  <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
                    <Typography color="text.secondary">
                      No Members found
                    </Typography>
                  </Box>
                </TableCell>
              </TableRow>
            ) : (
              
            
            data.map((member) => (
              <StyledTableRow key={member.id}>
                <StyledTableCell>
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    {member.profile_picture ? (
                      <img 
                        src={`${API_ENDPOINTS.BASE_URL}${member.profile_picture}`} 
                        alt={member.member_name}
                        style={{ 
                          width: 40, 
                          height: 40, 
                          borderRadius: '50%',
                          marginRight: '10px'
                        }} 
                      />
                    ) : (
                      <Box
                        sx={{
                          width: 40,
                          height: 40,
                          borderRadius: '50%',
                          backgroundColor: 'primary.main',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          marginRight: '10px',
                          color: 'common.white'
                        }}
                      >
                        {member.member_name.charAt(0)}
                      </Box>
                    )}
                  </Box>
                </StyledTableCell>
                <StyledTableCell>
                  <Typography sx={{ 
                    color: theme.palette.mode === 'dark' ? "common.white" : "common.black",
                    fontSize: "0.875rem",
                  }}>
                    {member.member_name}
                  </Typography>
                </StyledTableCell>
                
                <StyledTableCell>
                  <Typography sx={{ 
                    color: theme.palette.mode === 'dark' ? "common.white" : "common.black",
                    fontSize: "0.875rem",
                  }}>
                    ₹{member.thank_you_amount_sum.toLocaleString('en-IN')}
                  </Typography>
                </StyledTableCell>
                <StyledTableCell>
                  <Typography sx={{ 
                    color: theme.palette.mode === 'dark' ? "common.white" : "common.black",
                    fontSize: "0.875rem",
                  }}>
                    {member.referral_count}
                  </Typography>
                </StyledTableCell>
                <StyledTableCell>
                  <Typography sx={{ 
                    color: theme.palette.mode === 'dark' ? "common.white" : "common.black",
                    fontSize: "0.875rem",
                  }}>
                    {member.one_to_one_count}
                  </Typography>
                </StyledTableCell>
                <StyledTableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    {member.renewal_date?.expiry_date ? (
                      <Tooltip
                        title={
                          <Box sx={{ p: 1 }}>
                            <Typography variant="body2" component="div">
                              Purchase Date: {new Date(member.renewal_date.purchase_date).toLocaleDateString('en-IN', {
                                year: 'numeric',
                                month: 'long',
                                day: 'numeric'
                              })}
                            </Typography>
                            {member.renewal_date.remarks && (
                              <Typography variant="caption" component="div" sx={{ mt: 0.5, opacity: 0.8 }}>
                                Remarks: {member.renewal_date.remarks}
                              </Typography>
                            )}
                          </Box>
                        }
                        arrow
                        placement="top"
                      >
                        <Button
                          variant="outlined"
                          onClick={() => handleRenewalClick(member.id)}
                          sx={{
                            borderColor: new Date(member.renewal_date.expiry_date) < new Date() 
                              ? theme.palette.warning.main 
                              : theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.2)',
                            color: new Date(member.renewal_date.expiry_date) < new Date()
                              ? theme.palette.warning.main
                              : theme.palette.mode === 'dark' ? 'common.white' : 'common.black',
                            fontSize: "0.875rem",
                            textTransform: 'none',
                            '&:hover': {
                              borderColor: new Date(member.renewal_date.expiry_date) < new Date()
                                ? theme.palette.warning.dark
                                : theme.palette.primary.main,
                              backgroundColor: new Date(member.renewal_date.expiry_date) < new Date()
                                ? 'rgba(237, 108, 2, 0.08)'
                                : theme.palette.mode === 'dark' 
                                  ? 'rgba(25, 118, 210, 0.08)'
                                  : 'rgba(25, 118, 210, 0.04)',
                            },
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            minWidth: '180px',
                            justifyContent: 'flex-start',
                            padding: '6px 12px',
                            position: 'relative',
                            overflow: 'hidden',
                            ...(new Date(member.renewal_date.expiry_date) < new Date() && {
                              animation: 'pulse 2s infinite',
                              '@keyframes pulse': {
                                '0%': { opacity: 1 },
                                '50%': { opacity: 0.7 },
                                '100%': { opacity: 1 },
                              },
                            }),
                          }}
                        >
                          <Box
                            sx={{
                              width: 8,
                              height: 8,
                              borderRadius: '50%',
                              backgroundColor: new Date(member.renewal_date.expiry_date) < new Date()
                                ? theme.palette.warning.main
                                : 'success.main',
                              marginRight: 1,
                              ...(new Date(member.renewal_date.expiry_date) < new Date() && {
                                animation: 'blink 1s infinite',
                                '@keyframes blink': {
                                  '0%': { opacity: 1 },
                                  '50%': { opacity: 0.4 },
                                  '100%': { opacity: 1 },
                                },
                              }),
                            }}
                          />
                          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                            {new Date(member.renewal_date.expiry_date).toLocaleDateString('en-IN', {
                              year: 'numeric',
                              month: 'long',
                              day: 'numeric'
                            })}
                            {new Date(member.renewal_date.expiry_date) < new Date() && (
                              <Typography
                                variant="caption"
                                sx={{
                                  color: theme.palette.warning.main,
                                  fontWeight: 500,
                                  fontSize: '0.75rem',
                                }}
                              >
                                Membership Expired
                              </Typography>
                            )}
                          </Box>
                        </Button>
                      </Tooltip>
                    ) : (
                      <Button
                        variant="outlined"
                        onClick={() => handleRenewalClick(member.id)}
                        sx={{
                          borderColor: theme.palette.error.main,
                          color: theme.palette.error.main,
                          fontSize: "0.875rem",
                          textTransform: 'none',
                          '&:hover': {
                            borderColor: theme.palette.error.dark,
                            backgroundColor: theme.palette.mode === 'dark'
                              ? 'rgba(211, 47, 47, 0.08)'
                              : 'rgba(211, 47, 47, 0.04)',
                          },
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                          minWidth: '180px',
                          justifyContent: 'flex-start',
                          padding: '6px 12px',
                        }}
                      >
                        <Box
                          sx={{
                            width: 8,
                            height: 8,
                            borderRadius: '50%',
                            backgroundColor: 'error.main',
                            marginRight: 1,
                            animation: 'blink 1s infinite',
                            '@keyframes blink': {
                              '0%': { opacity: 1 },
                              '50%': { opacity: 0.4 },
                              '100%': { opacity: 1 },
                            },
                          }}
                        />
                        Set Expiry Date
                      </Button>
                    )}
                  </Box>
                </StyledTableCell>
                <StyledTableCell align="center">
                  <Tooltip title="Edit Member" arrow placement="top">
                    <IconButton
                      size="small"
                      sx={{ 
                        color: theme.palette.mode === 'dark' ? "white" : "rgba(0, 0, 0, 0.7)",
                        '&:hover': {
                          backgroundColor: theme.palette.mode === 'dark' 
                            ? 'rgba(255, 255, 255, 0.1)'
                            : 'rgba(0, 0, 0, 0.1)'
                        }
                      }}
                      onClick={() => handleEditClick(member.id)}
                    >
                      <Edit fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </StyledTableCell>
                
              </StyledTableRow>
            )))}
          </TableBody>
        </Table>

        <AddMemberModal
          open={openMemberModal}
          onClose={() => {
            setOpenMemberModal(false);
            setEditData(null);
          }}
          edit={Boolean(editData)}
          data={editData}
          onSuccess={onRefresh}
          chapterId={chapterId}
        />

        <RenewalModal
          open={openRenewalModal}
          onClose={() => {
            setOpenRenewalModal(false);
            setSelectedMemberId(null);
          }}
          memberId={selectedMemberId}
          onSuccess={onRefresh}
        />
      </TableContainer>
    </Box>
  );
};

export default MembersDataTable;

